import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useWebsocket } from 'contexts/websocket';
import { omit } from 'lodash';
import { createContext, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import feedbackService from 'services/feedback';
import { getAIPreview } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { getAccount } = useAuth();
    const { course, currentAssignmentId, currentAssignment, feedback, setFeedback } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const params = useParams();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const { onMessage } = useWebsocket();

    const [state, dispatch] = useReducer(
        (state, action) => {
            switch (action.type) {
                case 'SET_SELECTED_FEEDBACK_ID':
                    return {
                        selectedFeedbackId: action.payload,
                        selectedInlineCommentId: null,
                    };
                case 'SET_SELECTED_FEEDBACK_ID_TO_FIRST':
                    const target = feedback.find(({ id }) => id === params.feedbackId);
                    if (!target?.feedback?.improvement_suggestions?.filter(({ active }) => active).length) return state;
                    return typeof target.feedback === 'string'
                        ? state
                        : {
                              selectedFeedbackId: target.feedback.improvement_suggestions.filter(
                                  ({ active }) => active
                              )[0].id,
                              selectedInlineCommentId: null,
                          };
                case 'SET_SELECTED_INLINE_COMMENT_ID':
                    return {
                        selectedFeedbackId: null,
                        selectedInlineCommentId: action.payload,
                    };
                default:
                    return state;
            }
        },
        {
            selectedFeedbackId: null,
            selectedInlineCommentId: null,
        }
    );

    const { selectedFeedbackId, selectedInlineCommentId } = state;
    const setSelectedFeedbackId = (id) => dispatch({ type: 'SET_SELECTED_FEEDBACK_ID', payload: id });
    const setSelectedInlineCommentId = (id) => dispatch({ type: 'SET_SELECTED_INLINE_COMMENT_ID', payload: id });

    const [introductionDialog, setIntroductionDialog] = useState({ open: false });
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    const currentFeedback = feedback?.find(({ id }) => id === params.feedbackId);
    const currentFeedbackIndex = feedback?.findIndex(({ id }) => id === currentFeedback.id);

    const getHighlights = () => {
        let highlights = [];

        if (selectedFeedbackId) {
            highlights =
                currentFeedback?.feedback?.feedback_type === 'rubric'
                    ? currentFeedback?.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId)
                          ?.negative?.locations || []
                    : currentFeedback?.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId)
                          ?.locations || [];
        } else if (selectedInlineCommentId) {
            highlights = currentFeedback.inline_comments
                .filter(({ id }) => id === selectedInlineCommentId)
                .map(({ highlight }) => highlight);
        }

        return highlights.map((item) => ({ id: selectedInlineCommentId ?? selectedFeedbackId, value: item }));
    };

    const onClickBack = () => {
        history.push(`/assignments/${currentAssignmentId}`);
    };

    const onClickInfo = () => {
        setIntroductionDialog({ open: true });
    };

    const onClickPrevAttempt = () => {
        const currentFeedbackIndex = feedback.findIndex(({ id }) => id === params.feedbackId);
        const targetFeedback = feedback[currentFeedbackIndex - 1];
        history.push(`/assignments/${currentAssignmentId}/feedback/${targetFeedback.id}`);
    };

    const onClickNextAttempt = () => {
        const currentFeedbackIndex = feedback.findIndex(({ id }) => id === params.feedbackId);
        if (currentFeedbackIndex === feedback.length - 1) {
            history.push(`/assignments/${currentAssignmentId}/feedback`);
        } else {
            const targetFeedback = feedback[currentFeedbackIndex + 1];
            history.push(`/assignments/${currentAssignmentId}/feedback/${targetFeedback.id}`);
        }
    };

    const onClickRegenerate = () => {
        setDialog({
            open: true,
            message: t('dialog.regenerate-feedback.title', 'Regenerate Feedback?'),
            description: t(
                'dialog.regenerate-feedback.description',
                'This will permanently delete all existing feedback for this draft, and cannot be undone.'
            ),
            note: '',
            variant: 'primary',
            actionButtonText: t('common:controls.regenerate', 'Regenerate'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                closeDialog();
                setIsGenerating(true);
                try {
                    const account = await getAccount();
                    await getAIPreview({
                        target: 'feedback',
                        course_id: course.id,
                        owner_account_id: currentAssignment.owner_account_id,
                        assignment: currentAssignment,
                        submission: {
                            ...omit(currentFeedback, ['feedback', 'grading', 'inline_comments', 'submission']),
                            data: currentFeedback.submission,
                            ai: {
                                grading: currentFeedback.grading,
                                feedback: currentFeedback.feedback,
                                inline_comments: currentFeedback.inline_comments,
                            },
                        },
                        connections: account.connections.map(({ id }) => id),
                        language: course.language ?? 'English',
                    });
                } catch (e) {
                    console.error(e);
                    message.error(
                        t('common:message.something-went-wrong-try-again', 'Something went wrong. Please try again.')
                    );
                }
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickThumbsUp = ({ id: targetId }) => {
        const timestamp = new Date().getTime();
        setFeedback((current) => {
            return current.map((feedback) => {
                if (feedback.id !== currentFeedback.id) return feedback;
                const updatedFeedback = {
                    ...currentFeedback,
                    modified_on: timestamp,
                    feedback: {
                        ...currentFeedback.feedback,
                        improvement_suggestions: currentFeedback.feedback.improvement_suggestions.map((item) => {
                            if (item.id !== targetId) return item;
                            return {
                                ...item,
                                agree: !Boolean(item.agree),
                                disagree: false,
                            };
                        }),
                    },
                };
                feedbackService.updateFeedback(updatedFeedback.id, omit(updatedFeedback, ['_id']));
                return updatedFeedback;
            });
        });
    };

    const onClickThumbsDown = ({ id: targetId }) => {
        const timestamp = new Date().getTime();
        setFeedback((current) => {
            return current.map((feedback) => {
                if (feedback.id !== currentFeedback.id) return feedback;
                const updatedFeedback = {
                    ...currentFeedback,
                    modified_on: timestamp,
                    feedback: {
                        ...currentFeedback.feedback,
                        improvement_suggestions: currentFeedback.feedback.improvement_suggestions.map((item) => {
                            if (item.id !== targetId) return item;
                            return {
                                ...item,
                                agree: false,
                                disagree: !Boolean(item.disagree),
                            };
                        }),
                    },
                };
                feedbackService.updateFeedback(updatedFeedback.id, omit(updatedFeedback, ['_id']));
                return updatedFeedback;
            });
        });
    };

    useEffect(() => {
        const cleanup = onMessage(async (message) => {
            if (message.data?.payload_type === 'regenerate_submission') {
                const { submission } = message.data.payload;
                if (submission.file.key === currentFeedback.submission.key) {
                    const updatedFeedback = {
                        ...currentFeedback,
                        modified_on: new Date().getTime(),
                        feedback: submission.data.feedback,
                    };
                    setFeedback((current) => {
                        return current.map((feedback) => {
                            if (feedback.id !== updatedFeedback.id) return feedback;
                            feedbackService.updateFeedback(updatedFeedback.id, omit(updatedFeedback, ['_id']));
                            return updatedFeedback;
                        });
                    });
                    dispatch({ type: 'SET_SELECTED_FEEDBACK_ID_TO_FIRST', payload: updatedFeedback.id });
                    setIsGenerating(false);
                }
            }
        });
        return cleanup;
    }, [currentFeedback, onMessage, setFeedback]);

    return (
        <PageContext.Provider
            value={{
                dispatch,
                currentFeedback,
                currentFeedbackIndex,
                highlights: getHighlights(),
                selectedFeedbackId,
                setSelectedFeedbackId,
                selectedInlineCommentId,
                setSelectedInlineCommentId,
                introductionDialog,
                setIntroductionDialog,
                isGenerating,
                setIsGenerating,
                isDocumentFullScreen,
                setIsDocumentFullScreen,
                showDetails,
                setShowDetails,
                onClickBack,
                onClickInfo,
                onClickPrevAttempt,
                onClickNextAttempt,
                onClickRegenerate,
                onClickThumbsUp,
                onClickThumbsDown,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
